import type {
  AttentionEngine,
  AttentionDirection,
  AttentionSample,
} from "./attention-engine";

const WASM_ROOT =
  "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.35/wasm";
const MODEL_ASSET =
  "https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task";

type FaceLandmarkerInstance = Awaited<
  ReturnType<
    (typeof import("@mediapipe/tasks-vision"))["FaceLandmarker"]["createFromOptions"]
  >
>;

type Pose = { yaw: number; pitch: number };

function directionFromPose(yaw: number, pitch: number): AttentionDirection {
  const horizontal = Math.abs(yaw);
  const vertical = Math.abs(pitch);
  if (horizontal < 0.1 && vertical < 0.12) return "center";
  if (horizontal >= vertical) return yaw > 0 ? "right" : "left";
  return pitch > 0 ? "down" : "up";
}

function poseFromLandmarks(
  landmarks: Array<{ x: number; y: number }>,
): Pose | null {
  const nose = landmarks[1];
  const firstEye = landmarks[33];
  const secondEye = landmarks[263];
  if (!nose || !firstEye || !secondEye) return null;
  const eyeDistance = Math.hypot(
    secondEye.x - firstEye.x,
    secondEye.y - firstEye.y,
  );
  if (eyeDistance < 0.03) return null;
  const eyeMidX = (firstEye.x + secondEye.x) / 2;
  const eyeMidY = (firstEye.y + secondEye.y) / 2;
  return {
    yaw: (nose.x - eyeMidX) / eyeDistance,
    pitch: (nose.y - eyeMidY) / eyeDistance,
  };
}

function averagePose(poses: Pose[]): Pose {
  return poses.reduce(
    (total, pose) => ({
      yaw: total.yaw + pose.yaw / poses.length,
      pitch: total.pitch + pose.pitch / poses.length,
    }),
    { yaw: 0, pitch: 0 },
  );
}

/**
 * On-device only Face Landmarker adapter.
 *
 * The video element is processed synchronously by MediaPipe and is never
 * copied, retained or transmitted. Only the semantic sample leaves this
 * adapter. This is a head-pose proxy, not identity recognition and not proof
 * that the user is or is not productive.
 */
export function createMediaPipeAttentionEngine(): AttentionEngine {
  let landmarker: FaceLandmarkerInstance | null = null;
  let loading: Promise<FaceLandmarkerInstance> | null = null;
  let calibration: Pose[] = [];
  let baseline: Pose | null = null;

  const load = async () => {
    if (landmarker) return landmarker;
    if (!loading) {
      loading = import("@mediapipe/tasks-vision").then(
        async ({ FaceLandmarker, FilesetResolver }) => {
          const vision = await FilesetResolver.forVisionTasks(WASM_ROOT);
          return FaceLandmarker.createFromOptions(vision, {
            baseOptions: { modelAssetPath: MODEL_ASSET },
            runningMode: "VIDEO",
            numFaces: 1,
            minFaceDetectionConfidence: 0.55,
            minFacePresenceConfidence: 0.55,
            minTrackingConfidence: 0.5,
            outputFaceBlendshapes: false,
            outputFacialTransformationMatrixes: false,
          });
        },
      );
    }
    landmarker = await loading;
    return landmarker;
  };

  return {
    async sample({ documentVisible, video }): Promise<AttentionSample> {
      const at = Date.now();
      if (!documentVisible) {
        return {
          at,
          score: 0,
          confidence: 1,
          state: "away",
          direction: "not-visible",
          reason: "document-hidden",
        };
      }
      if (!video || video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
        return {
          at,
          score: 0,
          confidence: 0,
          state: "unknown",
          direction: "unknown",
          reason: "video-unavailable",
        };
      }
      if (!landmarker) {
        try {
          await load();
        } catch {
          return {
            at,
            score: 50,
            confidence: 0,
            state: "unknown",
            direction: "unknown",
            reason: "model-unavailable",
          };
        }
      }

      const result = landmarker!.detectForVideo(video, performance.now());
      const landmarks = result.faceLandmarks[0];
      if (!landmarks) {
        return {
          at,
          score: 0,
          confidence: 0.85,
          state: "away",
          direction: "not-visible",
          reason: "no-face",
        };
      }

      const pose = poseFromLandmarks(landmarks);
      if (!pose) {
        return {
          at,
          score: 50,
          confidence: 0.25,
          state: "unknown",
          direction: "unknown",
          reason: "calibrating",
        };
      }
      if (!baseline) {
        calibration.push(pose);
        if (calibration.length >= 15) baseline = averagePose(calibration);
        return {
          at,
          score: 50,
          confidence: calibration.length / 15,
          state: "unknown",
          direction: "unknown",
          reason: "calibrating",
        };
      }

      const yawDistance = Math.abs(pose.yaw - baseline.yaw);
      const pitchDistance = Math.abs(pose.pitch - baseline.pitch);
      const gazeDirection = directionFromPose(
        pose.yaw - baseline.yaw,
        pose.pitch - baseline.pitch,
      );
      const distance = Math.max(yawDistance / 0.22, pitchDistance / 0.28);
      const forward = distance <= 1;
      return {
        at,
        score: Math.max(0, Math.min(100, Math.round(100 - distance * 38))),
        confidence: Math.max(0.45, Math.min(0.95, 0.9 - distance * 0.12)),
        state: forward ? "focused" : "away",
        direction: gazeDirection,
        reason: forward ? "face-forward" : "head-turned",
      };
    },
    async reset() {
      calibration = [];
      baseline = null;
      loading = null;
      landmarker?.close();
      landmarker = null;
    },
  };
}
